// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState } from 'react';
import { ListItem, FormControlLabel, Typography, Box, Checkbox, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { useTheme } from '@mui/system';
import { useAppSelector } from 'Hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import paperClipIcon from 'Assets/paper-clip.png';
import LightTooltip, { WideLightTooltip } from 'Components/Tooltip';
import { useSelector } from 'react-redux';
import { Warning, Logout, Error } from '@mui/icons-material';
import { selectDependencyNames } from 'Features/bundle/bundleSelectors';
import stylesDef from './styles';

interface LayerListItemProps {
  item: IDeliverable;
  activeOrder: any;
  ugAlaCarteBoolean: boolean;
  onAddOnsClick: (selectedList: { item: IDeliverable, checked: boolean, typeFactor3D?:boolean }[]) => void;
  isUnavailable: boolean;
}

const LayerListItem = ({ item, activeOrder, ugAlaCarteBoolean, onAddOnsClick, isUnavailable }: LayerListItemProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);

  const { selectedAdditionals, selectedBundle, attributeAddOnsList, allAttributeAddOnsSelected } = useAppSelector((state) => state.bundle);
  const { imageryRequest } = useAppSelector((state) => state.project.project);
  const [type, setType] = useState('2D');

  const { amtChild } = useFlags();
  const { type: bundleType } = selectedBundle || {};
  const showCredit = !amtChild && bundleType !== 'utilities';
  const dependencies = useSelector((state) => selectDependencyNames(state, item));
  const isAttributeAddOn = attributeAddOnsList.some((addOn) => Number(addOn) === item.deliverableId);

  // Check if layer should be disabled due to nearmap confidence
  const isLayerDisabled = item.nearmapConfidence === 'none' && !!imageryRequest;

  useEffect(() => {
    const selectedAddOn = selectedAdditionals.find((i) => i._id === item._id);
    if (selectedAddOn) {
      setType(selectedAddOn.is3D ? '3D' : '2D');
    }
  }, [selectedAdditionals]);

  const handleCheck = (addOnItem:any, checked:boolean) => {
    // Prevent selection if layer is disabled
    if (isLayerDisabled) {
      return;
    }

    if (type === '3D') {
      onAddOnsClick([{ item: addOnItem, checked, typeFactor3D: true }]);
    } else {
      onAddOnsClick([{ item: addOnItem, checked, typeFactor3D: false }]);
    }
  };

  const handleSwitchChange = (newType: string) => {
    // Prevent type switching if layer is disabled
    if (isLayerDisabled) {
      return;
    }

    const checked = selectedAdditionals.findIndex((i) => i._id === item._id) !== -1;

    if (!checked) {
      setType(newType);
    } else if (newType === '3D') {
      onAddOnsClick([{ item, checked: false, typeFactor3D: false }, { item, checked, typeFactor3D: true }]); // remove the 2D cost and add the 3D cost
    } else if (newType === '2D') {
      onAddOnsClick([{ item, checked: false, typeFactor3D: true }, { item, checked, typeFactor3D: false }]); // remove the 3D cost and add the 2D cost
    }
  };

  const handleGoToTop = (event: React.MouseEvent<HTMLOrSVGElement>) => {
    event.stopPropagation();
    // Scroll to the top of the dialog when the attribute add-on icon is clicked
    const dialog = document.querySelector('.MuiDialog-paper')
    if (dialog && allAttributeAddOnsSelected) {
      dialog.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getBoxStyle = () => {
    if (isUnavailable) return styles.unavailableDeliverables;
    return undefined;
  };

  return (
    <Box sx={getBoxStyle()}>
      <ListItem sx={styles.listItem}>
        <FormControlLabel
          label={(
            <Box style={styles.layerLabel}>
              {dependencies.length > 0 && (
                <WideLightTooltip
                  placement="bottom"
                  title={(
                    <>
                      This layer requires the selection of
                      {' '}
                      {dependencies.map((dep: any, index: number) => (
                        <React.Fragment key={dep}>
                          <b>{dep}</b>
                          {index < dependencies.length - 1 ? ', ' : ''}
                        </React.Fragment>
                      ))}
                      . These will be added to your scope for convenience.
                    </>
                  )}
                >
                  <img src={paperClipIcon} alt="tooltip" style={styles.paperClipIcon} />
                </WideLightTooltip>
              )}
              {item.nearmapConfidence === 'low' && imageryRequest && (
                <LightTooltip
                  title={<Typography color="textSecondary">
                    <b>Low Confidence</b> (may be delivered with reduced accuracy).
                  </Typography>}
                  placement="top"
                  arrow
                >
                  <Box sx={styles.warningBox}>
                    <Warning fontSize='small' color="warning" />
                  </Box>
                </LightTooltip>
              )}
              {item.nearmapConfidence === 'none' && imageryRequest && (
                <LightTooltip
                  title={(
                    <Typography color="textSecondary">
                      This layer cannot be delivered from aircraft imagery.
                    </Typography>
                  )}
                  placement="top"
                  arrow
                >
                  <Box sx={styles.warningBox}>
                    <Error fontSize="small" color="error" />
                  </Box>
                </LightTooltip>
              )}
              <Typography variant="body1" display="inline" sx={[styles.itemName, isLayerDisabled && { color: 'lightgray' }]}>{item.name}</Typography>
            </Box>
          )}
          control={(
            <Checkbox
              sx={styles.checked}
              checked={selectedAdditionals.findIndex((i) => i._id === item._id) !== -1}
              onChange={(e) => handleCheck(item, e.target.checked)}
              disabled={isLayerDisabled}
            />
          )}
        />

        {isAttributeAddOn && (
          <LightTooltip
            title={(
              <Typography color="textSecondary">
                This layer is required to switch the{' '}
                <b>Location/Dimension</b> output.
              </Typography>
            )}
            placement="top"
            arrow
          >
            <Logout
              fontSize="small"
              onClick={handleGoToTop}
              sx={allAttributeAddOnsSelected ? styles.attributeAddOnIcon : styles.disableAttributeAddOnIcon}
            />
          </LightTooltip>
        )}
        {ugAlaCarteBoolean && item.typeFactor.length > 1 ? (
          <>
            <ToggleButtonGroup
              value={type}
              exclusive
              onChange={(_event, value) => handleSwitchChange(value)}
              disabled={isLayerDisabled}
            >
              <ToggleButton
                value="2D"
                sx={type === '2D' ? styles.selected : styles.notSelected}
                disabled={type === '2D' || isLayerDisabled}
              >
                2D
              </ToggleButton>
              <ToggleButton
                value="3D"
                sx={type === '3D' ? styles.selected : styles.notSelected}
                disabled={type === '3D' || isLayerDisabled}
              >
                3D
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography sx={styles.right} component="span">
              {showCredit && `  ${(type === '2D' ? item.typeFactor[0] * activeOrder.acreage : item.typeFactor[1] * activeOrder.acreage).toFixed(2)} Credits`}
            </Typography>
          </>
        ) : (
          <Typography sx={styles.right} component="span">
            {showCredit && `  ${(item.typeFactor[0] * activeOrder.acreage).toFixed(2)} Credits`}
          </Typography>
        )}
      </ListItem>
    </Box>
  );
};

export default LayerListItem;
